import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { CreditCard, Users, Sparkles, Star, Trophy, Share2, Target, CalendarPlus, MapPin, Clock, LogIn, Send, ChevronRight, X, Copy, UserPlus, Radar, GraduationCap, Flame, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { LEVELS, CHARACTERS, TIER_META } from '../lib/levels';
import { useT } from '../lib/i18n';
import AppHeader from './AppHeader';
import GoalsView from './GoalsView';
import FamilyTab from './FamilyTab';
import ParentLearn from './ParentLearn';
import ParentBuddyRequests from './ParentBuddyRequests';

const getLevel = (points) => LEVELS.find((l) => points >= l.min && points <= l.max) || LEVELS[0];
const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const formatDateTime = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// ---------- Achievement Card ----------
const AchievementCard = ({ child, weekStart, weekEnd, t }) => {
  const character = CHARACTERS[child.character] || CHARACTERS.ouistiti;
  const tier = child.current_tier ? TIER_META[child.current_tier.key] : null;
  const share = async () => {
    const text = `🏆 ${child.name} — ${tier ? tier.label : 'learning'} week! ${child.questions_answered} questions · ${child.habits_completed} habits · ${child.level.name}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Family Achievement', text });
      else { await navigator.clipboard.writeText(text); toast.success('Copied!'); }
    } catch { /* cancelled */ }
  };
  return (
    <div className="rounded-3xl overflow-hidden shadow-squishyLg border-4 border-gold" data-testid={`achievement-card-${child.id}`}>
      <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gold flex items-center justify-center text-navy font-heading text-sm font-bold">O</div>
          <span className="font-heading font-bold text-sm">OuiStuPaStu</span>
        </div>
        <span className="text-xs text-white/60 font-semibold">{formatDate(weekStart)} – {formatDate(weekEnd)}</span>
      </div>
      <div className="bg-gradient-to-br from-cream to-white p-6 text-center">
        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-teal mb-3 animate-float">
          <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-navy">{child.name}</h3>
        <p className="text-navy/60 text-sm">with {character.name}</p>
        {tier ? (
          <div className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full font-heading font-bold text-navy" style={{ background: `${tier.color}25`, border: `2px solid ${tier.color}` }}>
            <span className="text-xl">{tier.emoji}</span> {tier.label}
          </div>
        ) : (
          <div className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full font-heading font-bold text-navy bg-slate-100 border-2 border-slate-200">
            <Target className="w-4 h-4" /> {t('parent.warming_up')}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white rounded-2xl p-3 border-2 border-cream">
            <div className="font-heading text-2xl font-bold text-teal">{child.questions_answered}</div>
            <div className="text-[10px] text-navy/60 uppercase font-bold tracking-wider mt-0.5">Q</div>
          </div>
          <div className="bg-white rounded-2xl p-3 border-2 border-cream">
            <div className="font-heading text-2xl font-bold text-gold">{child.habits_completed}</div>
            <div className="text-[10px] text-navy/60 uppercase font-bold tracking-wider mt-0.5">H</div>
          </div>
          <div className="bg-white rounded-2xl p-3 border-2 border-cream">
            <div className="font-heading text-2xl font-bold text-navy">{child.level.name.slice(0,6)}</div>
            <div className="text-[10px] text-navy/60 uppercase font-bold tracking-wider mt-0.5">L</div>
          </div>
        </div>
        {child.next_tier && (
          <div className="mt-5 text-left">
            <div className="flex items-center justify-between text-xs font-semibold text-navy/70 mb-1.5">
              <span>{t('parent.progress_to')} {child.next_tier.label}</span>
              <span>{Math.min(child.questions_answered, child.next_tier.min_q)}/{child.next_tier.min_q}Q · {Math.min(child.habits_completed, child.next_tier.min_h)}/{child.next_tier.min_h}H</span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal to-gold rounded-full transition-[width] duration-500"
                style={{ width: `${Math.min(100, ((child.questions_answered / Math.max(1, child.next_tier.min_q)) + (child.next_tier.min_h ? child.habits_completed / Math.max(1, child.next_tier.min_h) : 1)) / 2 * 100)}%` }} />
            </div>
          </div>
        )}
        <button onClick={share} className="mt-5 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-navy text-white font-heading font-bold hover:scale-[1.02] active:scale-95 transition-transform duration-200" data-testid={`share-achievement-${child.id}`}>
          <Share2 className="w-4 h-4" /> {t('parent.share')}
        </button>
      </div>
    </div>
  );
};

// ---------- Playdate Detail ----------
const PlaydateDetail = ({ playdate, onClose, onRefresh }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const scrollRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/playdates/${playdate.id}/messages`);
      setMessages(data.messages);
      setChatOpen(data.chat_open);
    } catch { /* silent */ }
  }, [playdate.id]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/playdates/${playdate.id}/messages`, { text });
      setText('');
      await loadMessages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const rsvp = async (status) => {
    try {
      await api.post(`/playdates/${playdate.id}/rsvp`, { status });
      toast.success('RSVP updated');
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'RSVP failed'); }
  };

  const copyCode = async () => {
    await navigator.clipboard.writeText(playdate.join_code);
    toast.success(`Code ${playdate.join_code} copied`);
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/playdates/${playdate.id}/invite`, { parent_email: inviteEmail });
      setInviteEmail('');
      toast.success('Invitation sent');
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Invite failed'); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-navy/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="playdate-detail-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="bg-navy text-white p-5 rounded-t-3xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-bold" data-testid="playdate-name">{playdate.name}</h2>
              <div className="text-white/70 text-xs mt-1 flex items-center gap-1.5"><Clock className="w-3 h-3" />{formatDateTime(playdate.start_at)} – {formatDateTime(playdate.end_at)}</div>
              <div className="text-white/70 text-xs mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" />{playdate.location}</div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="playdate-close-button"><X className="w-4 h-4" /></button>
          </div>
          {playdate.description && <p className="text-white/80 text-sm mt-3">{playdate.description}</p>}
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* RSVP */}
          {!playdate.is_host && !playdate.is_past && (
            <div>
              <div className="text-xs font-heading font-bold uppercase text-navy/60 mb-2">RSVP</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { s: 'accepted', label: '✅ Coming', color: 'teal' },
                  { s: 'maybe',    label: '🤔 Maybe',  color: 'gold' },
                  { s: 'declined', label: "❌ Can't",  color: 'navy' },
                ].map((o) => (
                  <button key={o.s} onClick={() => rsvp(o.s)}
                    className={`p-3 rounded-2xl border-2 font-heading font-bold text-sm transition-transform duration-200 hover:scale-105 active:scale-95 ${playdate.my_rsvp === o.s ? `border-${o.color} bg-${o.color}/10 text-navy` : 'border-slate-200 text-navy'}`}
                    data-testid={`rsvp-${o.s}`}>{o.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Invited families */}
          <div>
            <div className="text-xs font-heading font-bold uppercase text-navy/60 mb-2 flex items-center justify-between">
              <span>Families ({playdate.accepted_count}/{playdate.max_families})</span>
              <span className="text-teal">Code: {playdate.join_code}
                <button onClick={copyCode} className="ml-1 align-middle text-teal hover:opacity-70" data-testid="copy-code-btn"><Copy className="inline w-3 h-3" /></button>
              </span>
            </div>
            <div className="space-y-2">
              {(playdate.invites || []).map((i) => (
                <div key={i.id} className="flex items-center justify-between p-3 rounded-2xl bg-cream" data-testid={`invite-row-${i.id}`}>
                  <span className="font-semibold text-navy text-sm truncate">{i.invited_parent_name}</span>
                  <span className={`text-xs font-heading font-bold px-2 py-0.5 rounded-full ${i.status === 'accepted' ? 'bg-teal/15 text-teal' : i.status === 'declined' ? 'bg-red-100 text-red-600' : i.status === 'maybe' ? 'bg-gold/15 text-gold' : 'bg-slate-200 text-navy/60'}`}>{i.status}</span>
                </div>
              ))}
            </div>
            {playdate.is_host && !playdate.is_past && (
              <form onSubmit={invite} className="mt-3 flex gap-2" data-testid="invite-form">
                <input type="email" required placeholder="parent@email.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1 h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm" data-testid="invite-email-input" />
                <button type="submit" className="h-11 px-4 rounded-xl bg-navy text-white font-heading font-bold text-sm flex items-center gap-1 hover:scale-105 transition-transform" data-testid="invite-submit"><UserPlus className="w-4 h-4" /> Invite</button>
              </form>
            )}
          </div>

          {/* Chat */}
          <div>
            <div className="text-xs font-heading font-bold uppercase text-navy/60 mb-2">Event Chat {!chatOpen && <span className="text-red-500 normal-case">(closed)</span>}</div>
            <div ref={scrollRef} className="rounded-2xl bg-cream p-3 h-60 overflow-y-auto space-y-2" data-testid="playdate-chat">
              {messages.length === 0 && <p className="text-navy/50 text-sm text-center py-6">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.user_id === user.id ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl ${m.user_id === user.id ? 'bg-navy text-white' : 'bg-white text-navy border-2 border-cream'}`}>
                    {m.user_id !== user.id && <div className="font-heading font-bold text-[10px] opacity-70">{m.user_name}</div>}
                    <div className="text-sm">{m.text}</div>
                  </div>
                </div>
              ))}
            </div>
            {chatOpen && (
              <form onSubmit={send} className="mt-2 flex gap-2" data-testid="chat-form">
                <input type="text" placeholder="Type a message…" value={text} onChange={(e) => setText(e.target.value)}
                  className="flex-1 h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm" data-testid="chat-input" />
                <button type="submit" disabled={sending} className="h-11 w-11 rounded-xl bg-teal text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-60" data-testid="chat-send"><Send className="w-4 h-4" /></button>
              </form>
            )}
            {playdate.is_past && (
              <p className="text-xs text-navy/60 mt-2">🎉 You met {playdate.accepted_count} {playdate.accepted_count === 1 ? 'family' : 'families'} at this playdate.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Playdate Create ----------
const CreatePlaydateForm = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({ name: '', description: '', start_at: '', end_at: '', location: '', max_families: 4 });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        max_families: Number(form.max_families),
      };
      const { data } = await api.post('/playdates', payload);
      toast.success('Playdate created 🎉');
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-3xl p-5 border-2 border-teal shadow-squishy space-y-3" data-testid="create-playdate-form">
      <h3 className="font-heading text-lg font-bold text-navy">Plan a Playdate</h3>
      <input required placeholder="Event name" value={form.name} onChange={(e) => upd('name', e.target.value)}
        className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-name" />
      <textarea placeholder="Description (optional)" value={form.description} onChange={(e) => upd('description', e.target.value)}
        rows={2} className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-desc" />
      <input required placeholder="Location or address" value={form.location} onChange={(e) => upd('location', e.target.value)}
        className="w-full h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-location" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-navy/60 mb-1">Start</label>
          <input required type="datetime-local" value={form.start_at} onChange={(e) => upd('start_at', e.target.value)}
            className="w-full h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-start" />
        </div>
        <div>
          <label className="block text-xs text-navy/60 mb-1">End</label>
          <input required type="datetime-local" value={form.end_at} onChange={(e) => upd('end_at', e.target.value)}
            className="w-full h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-end" />
        </div>
      </div>
      <div>
        <label className="block text-xs text-navy/60 mb-1">Max families</label>
        <input type="number" min={2} max={10} value={form.max_families} onChange={(e) => upd('max_families', e.target.value)}
          className="w-full h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none" data-testid="pd-max" />
      </div>
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 h-12 rounded-full bg-white border-2 border-navy text-navy font-heading font-bold" data-testid="pd-cancel">Cancel</button>
        <button type="submit" disabled={busy} className="flex-1 h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60" data-testid="pd-submit">{busy ? 'Creating…' : 'Create'}</button>
      </div>
    </form>
  );
};

// ---------- Playdate Row ----------
const PlaydateRow = ({ p, onOpen }) => {
  const badgeColor = p.is_past ? 'bg-slate-200 text-navy/60' : p.my_rsvp === 'accepted' ? 'bg-teal/15 text-teal' : p.my_rsvp === 'pending' ? 'bg-gold/20 text-gold' : 'bg-slate-100 text-navy';
  return (
    <button onClick={() => onOpen(p)}
      className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border-2 border-cream hover:border-teal transition-colors duration-200 text-left"
      data-testid={`playdate-row-${p.id}`}>
      <div className="w-12 h-12 rounded-2xl bg-navy flex items-center justify-center text-gold shrink-0">
        <CalendarPlus className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-navy truncate">{p.name}</div>
        <div className="text-xs text-navy/60 mt-0.5 flex items-center gap-1 truncate"><Clock className="w-3 h-3" />{formatDateTime(p.start_at)}</div>
        <div className="text-xs text-navy/60 flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{p.location}</div>
      </div>
      <div className="text-right shrink-0">
        <span className={`text-[11px] font-heading font-bold px-2 py-0.5 rounded-full ${badgeColor}`}>{p.is_past ? 'Past' : (p.my_rsvp || 'pending')}</span>
        <div className="text-xs text-navy/50 mt-1">{p.accepted_count}/{p.max_families} 👨‍👩‍👧</div>
      </div>
      <ChevronRight className="w-4 h-4 text-navy/30 shrink-0" />
    </button>
  );
};

// ---------- Main Parent Dashboard ----------
function ParentGoalsTab({ kids, t }) {
  const [selectedKid, setSelectedKid] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (kids.length > 0 && !selectedKid) setSelectedKid(kids[0]);
  }, [kids, selectedKid]);

  useEffect(() => {
    if (!selectedKid) return;
    setLoading(true);
    api.get(`/parent/child-goals/${selectedKid.id}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load goals'))
      .finally(() => setLoading(false));
  }, [selectedKid]);

  if (kids.length === 0) {
    return (
      <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream text-center" data-testid="parent-goals-empty">
        <Target className="w-10 h-10 mx-auto text-navy/40 mb-2" />
        <p className="font-heading font-bold text-navy">No children linked yet</p>
        <p className="text-navy/60 text-sm mt-1">Once your child signs up with your email, their goals will appear here.</p>
      </section>
    );
  }

  return (
    <div className="space-y-5" data-testid="parent-goals-tab">
      {kids.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {kids.map((k) => {
            const c = CHARACTERS[k.character] || CHARACTERS.stupidopoulou;
            const active = selectedKid?.id === k.id;
            return (
              <button key={k.id} onClick={() => setSelectedKid(k)}
                className={`inline-flex items-center gap-2 pl-1 pr-4 py-1 rounded-full border-2 font-heading font-bold text-sm transition-colors ${active ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-slate-200 hover:border-teal'}`}
                data-testid={`kid-tab-${k.id}`}>
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white">
                  <img src={c.image} alt="" className="w-full h-full object-cover" />
                </div>
                {k.name}
              </button>
            );
          })}
        </div>
      )}
      {loading ? (
        <div className="p-6 text-center text-navy/60 font-heading">Loading goals…</div>
      ) : (
        <GoalsView readOnly initialData={data} title={selectedKid ? `${selectedKid.name}'s Goals` : 'Goals'} />
      )}
    </div>
  );
}

export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [tab, setTab] = useState('overview');
  const [kids, setKids] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subBusy, setSubBusy] = useState(false);

  const [playdates, setPlaydates] = useState([]);
  const [selectedPd, setSelectedPd] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [families, setFamilies] = useState([]);
  const [tutorNotes, setTutorNotes] = useState([]);

  const loadCore = useCallback(async () => {
    try {
      const [c, s, tn] = await Promise.all([
        api.get('/parent/children'),
        api.get('/parent/weekly-summary'),
        api.get('/parent/tutor-notes').catch(() => ({ data: { notes: [] } })),
      ]);
      setKids(c.data.children); setSummary(s.data); setTutorNotes(tn.data.notes || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const loadPlaydates = useCallback(async () => {
    try {
      const { data } = await api.get('/playdates');
      setPlaydates(data.playdates);
      if (selectedPd) {
        const fresh = data.playdates.find((x) => x.id === selectedPd.id);
        if (fresh) {
          const { data: d } = await api.get(`/playdates/${fresh.id}`);
          setSelectedPd(d);
        }
      }
    } catch { /* silent */ }
  }, [selectedPd]);

  const loadFamilies = useCallback(async () => {
    try {
      const { data } = await api.get('/families-near-me');
      setFamilies(data.families || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => { if (tab === 'playdates') { loadPlaydates(); loadFamilies(); } }, [tab, loadPlaydates, loadFamilies]);

  const openPd = async (p) => {
    try { const { data } = await api.get(`/playdates/${p.id}`); setSelectedPd(data); }
    catch { toast.error('Could not open playdate'); }
  };

  const subscribe = async () => {
    setSubBusy(true);
    try {
      const { data } = await api.post('/payments/checkout', { lookup_key: 'ouistupastu_monthly', origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); setSubBusy(false); }
  };

  const toggleDiscovery = async (val) => {
    try {
      await api.patch('/user/preferences', { discovery_enabled: val });
      await refresh();
      toast.success(val ? 'You are now discoverable' : 'Discovery off');
    } catch { toast.error('Failed'); }
  };

  const joinByCode = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    try {
      const { data } = await api.post(`/playdates/join/${joinCode.trim().toUpperCase()}`);
      toast.success(data.already_joined ? 'Already joined' : 'Joined!');
      setJoinCode('');
      loadPlaydates();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Join failed'); }
  };

  const nowIso = new Date().toISOString();
  const upcoming = playdates.filter((p) => !p.is_past && (p.is_host || p.my_rsvp === 'accepted'));
  const pending = playdates.filter((p) => !p.is_past && !p.is_host && (p.my_rsvp === 'pending' || p.my_rsvp === 'maybe'));
  const past = playdates.filter((p) => p.is_past);

  const totalBadges = summary?.total_badges || 0;
  const activeChildren = summary?.children || [];
  const topChild = activeChildren.slice().sort((a, b) => (b.questions_answered + b.habits_completed) - (a.questions_answered + a.habits_completed))[0];

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader subtitle={t('header.dashboard_parent')} />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-6" data-testid="parent-dashboard">
        {/* Tabs */}
        <div className="flex gap-2 bg-white rounded-full p-1 shadow-squishy border-2 border-cream w-fit" data-testid="parent-tabs">
          {[
            { k: 'overview', label: 'Overview' },
            { k: 'learn', label: 'Learn' },
            { k: 'goals', label: 'Goals' },
            { k: 'family', label: 'Family' },
            { k: 'playdates', label: 'Playdates' },
          ].map((tv) => (
            <button key={tv.k} onClick={() => setTab(tv.k)}
              className={`px-5 py-2 rounded-full font-heading font-bold text-sm transition-colors duration-200 ${tab === tv.k ? 'bg-navy text-white' : 'text-navy hover:bg-cream'}`}
              data-testid={`tab-${tv.k}`}>{tv.label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <>
            <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">{t('parent.welcome')} {user?.name?.split(' ')[0]} 👋</h1>
              <p className="text-navy/70 mt-1">{t('parent.subtitle')}</p>
            </section>

            <ParentBuddyRequests />

            {topChild && topChild.questions_answered > 0 && (
              <section className="bg-gradient-to-r from-gold/15 to-teal/15 border-2 border-gold rounded-3xl p-5 shadow-squishy flex items-center gap-4" data-testid="weekly-achievement-banner">
                <div className="text-4xl shrink-0">🏆</div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-navy leading-snug">
                    {topChild.name} {t('parent.week_headline', { tier: topChild.current_tier ? t(`parent.tier_${topChild.current_tier.key}`) : t('parent.tier_default') })}
                  </div>
                  <div className="text-sm text-navy/70 mt-0.5">
                    {t('parent.qh_summary', { level: topChild.level.name, q: topChild.questions_answered, h: topChild.habits_completed })}
                  </div>
                </div>
              </section>
            )}

            <section className="bg-navy rounded-3xl p-6 shadow-squishy text-white" data-testid="parent-badge-streak-card">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gold flex items-center justify-center shrink-0"><Trophy className="w-8 h-8 text-navy" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-gold font-heading font-semibold text-sm uppercase tracking-wider">{t('parent.badge_streak_kicker')}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-heading text-4xl font-bold" data-testid="parent-badge-count">{totalBadges}</span>
                    <span className="text-white/70 text-sm">{t('parent.badges_earned')}</span>
                  </div>
                  <p className="text-white/60 text-xs mt-1">{t('parent.badge_hint')}</p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="subscription-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-teal" />
                    <span className="text-teal font-heading font-semibold text-sm uppercase tracking-wider">{t('parent.subscription_kicker')}</span>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-navy">$9.99 {t('parent.plan_price')}</h2>
                  <p className="text-navy/70 text-sm mt-1 max-w-md">{t('parent.plan_desc')}</p>
                </div>
                {user?.subscription_active ? (
                  <span className="px-4 py-2 rounded-full bg-teal/15 text-teal font-heading font-bold text-sm" data-testid="subscription-active-badge">{t('parent.active')}</span>
                ) : (
                  <button onClick={subscribe} disabled={subBusy}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-navy font-heading font-bold shadow-gold hover:scale-105 active:scale-95 transition-transform duration-200 disabled:opacity-60"
                    data-testid="subscribe-button">
                    <CreditCard className="w-4 h-4" /> {subBusy ? t('parent.subscribe_loading') : t('parent.subscribe')}
                  </button>
                )}
              </div>
            </section>

            {tutorNotes.length > 0 && (
              <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="tutor-notes-card">
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="w-5 h-5 text-teal" />
                  <h2 className="font-heading text-xl font-bold text-navy">Tutor Session Notes</h2>
                </div>
                <div className="space-y-2">
                  {tutorNotes.slice(0, 5).map((n) => (
                    <div key={n.id} className="p-3 rounded-2xl bg-cream" data-testid={`tutor-note-${n.id}`}>
                      <div className="flex items-center justify-between text-xs text-navy/60 mb-1">
                        <span className="font-heading font-bold text-navy">{n.student_name}</span>
                        <span>{new Date(n.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-navy leading-snug">{n.summary}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!loading && activeChildren.length > 0 && (
              <section className="space-y-4" data-testid="achievement-cards-list">
                <div className="flex items-center gap-2 px-1">
                  <Star className="w-5 h-5 text-gold fill-current" />
                  <h2 className="font-heading text-xl font-bold text-navy">{t('parent.wins')}</h2>
                </div>
                {activeChildren.map((child) => (
                  <AchievementCard key={child.id} child={child} weekStart={summary.week_start} weekEnd={summary.week_end} t={t} />
                ))}
              </section>
            )}

            <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream" data-testid="children-list-card">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-teal" />
                <h2 className="font-heading text-xl font-bold text-navy">{t('parent.your_children')}</h2>
              </div>
              {loading ? <p className="text-navy/60">Loading…</p>
                : kids.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-navy/60 mb-2">{t('parent.no_children')}</p>
                  <p className="text-navy/50 text-sm">{t('parent.link_hint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {kids.map((kid) => {
                    const level = kid.level || getLevel(kid.points || 0);
                    const character = CHARACTERS[kid.character] || CHARACTERS.stupidopoulou;
                    return (
                      <div key={kid.id} className="flex items-center gap-4 p-4 rounded-2xl bg-cream" data-testid={`child-row-${kid.id}`}>
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-teal shrink-0">
                          <img src={character.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-bold text-navy">{kid.name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/20 text-navy text-xs font-bold">
                              <Star className="w-3 h-3 fill-current text-gold" /> {t(`level.${level.name.toLowerCase()}`)}
                            </span>
                            <span className="text-navy/60 text-xs">{kid.points || 0} {t('level.pts')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {tab === 'goals' && (
          <ParentGoalsTab kids={kids} t={t} />
        )}

        {tab === 'learn' && (
          <ParentLearn />
        )}

        {tab === 'family' && (
          <FamilyTab />
        )}

        {tab === 'playdates' && (
          <>
            {/* Discovery + create */}
            <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream space-y-3">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <button onClick={() => setShowCreate((v) => !v)}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gold text-navy font-heading font-bold shadow-gold hover:scale-105 active:scale-95 transition-transform"
                  data-testid="plan-playdate-button">
                  <CalendarPlus className="w-4 h-4" /> Plan a Playdate
                </button>
                <form onSubmit={joinByCode} className="flex gap-2" data-testid="join-code-form">
                  <input placeholder="Join code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    className="h-11 w-32 px-3 rounded-full border-2 border-slate-200 focus:border-teal focus:outline-none font-mono text-sm uppercase" data-testid="join-code-input" />
                  <button type="submit" className="h-11 px-4 rounded-full bg-navy text-white font-heading font-bold text-sm flex items-center gap-1" data-testid="join-code-submit"><LogIn className="w-4 h-4" /> Join</button>
                </form>
              </div>
              <label className="flex items-center gap-2 text-sm text-navy/80 cursor-pointer" data-testid="discovery-toggle">
                <input type="checkbox" checked={!!user?.discovery_enabled} onChange={(e) => toggleDiscovery(e.target.checked)} className="w-4 h-4 accent-teal" />
                <Radar className="w-4 h-4 text-teal" />
                <span>Let other families near me find our family</span>
              </label>
            </section>

            {showCreate && <CreatePlaydateForm onCreated={(p) => { setShowCreate(false); loadPlaydates(); setSelectedPd(p); }} onCancel={() => setShowCreate(false)} />}

            {/* Sections */}
            {[
              { key: 'upcoming', title: '🗓️ Upcoming', list: upcoming, empty: 'No upcoming playdates.' },
              { key: 'pending',  title: '📬 Pending Invitations', list: pending, empty: 'No pending invitations.' },
              { key: 'past',     title: '🕰️ Past Playdates', list: past, empty: 'No past playdates yet.' },
            ].map((sec) => (
              <section key={sec.key} data-testid={`playdate-section-${sec.key}`}>
                <h3 className="font-heading text-lg font-bold text-navy px-1 mb-2">{sec.title}</h3>
                {sec.list.length === 0 ? (
                  <p className="text-navy/50 text-sm px-1">{sec.empty}</p>
                ) : (
                  <div className="space-y-2">
                    {sec.list.map((p) => <PlaydateRow key={p.id} p={p} onOpen={openPd} />)}
                  </div>
                )}
              </section>
            ))}

            {/* Families near me */}
            <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="families-near-me-card">
              <div className="flex items-center gap-2 mb-3">
                <Radar className="w-5 h-5 text-teal" />
                <h3 className="font-heading text-lg font-bold text-navy">Families Near Me</h3>
              </div>
              {families.length === 0 ? (
                <p className="text-navy/60 text-sm">No other opted-in families in your country yet. Enable discovery above to appear here for others.</p>
              ) : (
                <div className="space-y-2">
                  {families.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 p-3 rounded-2xl bg-cream" data-testid={`family-row-${f.id}`}>
                      <div className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center font-heading font-bold">{(f.family_name || f.name || '?')[0]}</div>
                      <div className="font-heading font-semibold text-navy">{f.family_name || f.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {selectedPd && <PlaydateDetail playdate={selectedPd} onClose={() => setSelectedPd(null)} onRefresh={loadPlaydates} />}
    </div>
  );
}
